/**
 * One confirm/result sheet, replacing scattered window.confirm / prompt / alert.
 *
 * Why (Daniel 2026-07-27 audit): native dialogs can't show the thing they're
 * asking about. A `window.confirm` on "mark this deal withdrawn" can't render
 * the note field, can't show what changes, and its OK/Cancel wording is fixed,
 * so "Cancel" on a dialog about cancelling a deal is genuinely ambiguous.
 * `window.alert` is worse for results: Production reported team-wide email
 * blast outcomes as a multi-line alert dump.
 *
 * Deliberately small. This is not a modal framework. It is a confirm with an
 * optional note field and an optional destructive treatment, plus a result
 * mode for reporting what happened.
 *
 * Rollout 2026-08-04. Two additions, both driven by window.prompt call sites
 * the July pass didn't reach:
 *
 *   - `confirmPhrase`, type-to-confirm. Transaction delete made you type the
 *     street address into a window.prompt, which meant the string you had to
 *     match was embedded in the prompt message and a typo read back as a
 *     generic "didn't match" alert with no second chance.
 *   - `PromptSheet`, for the prompts that collect a VALUE (a link URL, a list
 *     name) rather than confirm a decision. Those can't be a ConfirmSheet
 *     note field: the value is required, and it needs validation before the
 *     sheet closes.
 *
 * ═══ 2026-08-12: all three sheets now portal to document.body ═══════════
 *
 * They used to return a bare `position: fixed; inset: 0` pair at zIndex 300.
 * Layout's `<main className="tr-page-anim">` runs a keyframe that animates
 * `transform`, and an element with a transform creates a containing block for
 * `position: fixed` descendants. So the backdrop and the panel were positioned
 * against the page column rather than the viewport: on contact detail the
 * backdrop covered only the left aside, and the panel centred itself in the
 * column instead of the screen.
 *
 * This is the repo's most repeated bug (see AGENTS.md, "Modals must portal to
 * document.body"). Three agents hit it on this file in two days and each one
 * wrapped its own createPortal around the call site instead of fixing the
 * component, which is how ShowingCalendarSettings ended up with a portal
 * around a portal. scripts/verify-modal-portals.py now fails the build on any
 * new bare overlay so the next person does not get the chance.
 *
 * Everything shared by the three sheets lives in SheetShell below: the portal,
 * the backdrop, focus handling, Escape, the scroll lock and the accessible
 * name. Divergence between three hand-copied shells is what let the old one
 * ship with no focus management at all.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ModalPortal } from './ModalPortal';
import { Textarea, TextInput } from './FormPrimitives';

/**
 * Above every other overlay in the app.
 *
 * A confirm can be raised from inside any of them, and a confirm you cannot
 * see is worse than no confirm: the page looks frozen and the action never
 * happens. The tallest things it has to clear are the dialer call sheet and
 * the attachments picker at 1100, and the rich text emoji popover at 1201.
 * Portalled siblings on document.body stack purely by z-index, so being
 * mounted "inside" another modal in the React tree buys nothing.
 */
const SHEET_Z = 1300;

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
    // offsetParent is null for display:none. Cheaper than getComputedStyle and
    // enough here, since nothing in these sheets is position:fixed.
    .filter((el) => el.offsetParent !== null);
}

/**
 * Open sheets, innermost last.
 *
 * Two things need to know about nesting. Escape must close only the topmost
 * sheet, otherwise confirming inside an open modal closes both at once. And
 * the body scroll lock must be released by the last sheet to close, not the
 * first, or the background starts scrolling again while a sheet is still up.
 */
const openSheets: object[] = [];
let bodyOverflowBeforeLock = '';

function useSheetBehavior(onDismiss: () => void) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dismissRef = useRef(onDismiss);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Kept in a ref so the mount effect below can stay on [] and not re-run
  // (and re-steal focus) every time the parent re-renders with a new closure.
  useEffect(() => {
    dismissRef.current = onDismiss;
  });

  useEffect(() => {
    const token = {};
    openSheets.push(token);

    // Remember where focus came from, but never remember a node inside this
    // sheet. StrictMode runs this effect twice in development: mount, clean
    // up, mount again. On the second pass focus is already on the panel, and
    // a naive capture would "restore" focus to an element that is about to be
    // removed, which reads as focus vanishing to the top of the page.
    const active = document.activeElement as HTMLElement | null;
    if (!panelRef.current?.contains(active)) restoreFocusRef.current = active;

    if (openSheets.length === 1) {
      bodyOverflowBeforeLock = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    // Move focus in, but do not fight autoFocus. The type-to-confirm input and
    // both PromptSheet inputs set autoFocus and React has already applied it by
    // the time this runs, so only take over when focus is still outside.
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      (focusableWithin(panel)[0] ?? panel).focus();
    }

    // Escape, in the CAPTURE phase, and it swallows the event.
    //
    // Not a detail. NewFromContractWizard has its own document-level Escape
    // that discards the whole shell, and it renders a ConfirmSheet asking
    // whether to discard. Registered normally, both handlers fire on one
    // press, and because the wizard mounted first it fires first: Escape on
    // the confirm would discard the thing the confirm was asking about.
    // stopPropagation during capture at document ends the event there, so
    // whatever is underneath never sees it. The same applies to the palette,
    // the notification bell and AskClaude, which all listen on window.
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (openSheets[openSheets.length - 1] !== token) return;
      e.stopPropagation();
      dismissRef.current();
    }
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      const i = openSheets.indexOf(token);
      if (i >= 0) openSheets.splice(i, 1);
      if (openSheets.length === 0) document.body.style.overflow = bodyOverflowBeforeLock;
      // Send focus back where it came from, so keyboard users land on the
      // button they opened the sheet with instead of at the top of the page.
      const restore = restoreFocusRef.current;
      if (restore && document.contains(restore)) restore.focus();
    };
  }, []);

  const onPanelKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const items = focusableWithin(panel);
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const outside = !panel.contains(active);
    if (e.shiftKey && (active === first || outside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || outside)) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  return { panelRef, onPanelKeyDown };
}

/**
 * The portal, the backdrop and the panel. Shared by all three sheets.
 *
 * Dismissal is on mousedown rather than click, and only when the press starts
 * on the backdrop itself. With a click handler, selecting text in the note
 * field and releasing the mouse past the edge of the panel retargets the click
 * to the backdrop and throws the note away.
 */
// Build 11 (2026-09-14): exported so FollowUpDoneSheet shares the frame.
export function SheetShell({
  labelledBy,
  width,
  onDismiss,
  children,
}: {
  labelledBy: string;
  width: number;
  onDismiss: () => void;
  children: React.ReactNode;
}) {
  const { panelRef, onPanelKeyDown } = useSheetBehavior(onDismiss);
  return (
    <ModalPortal>
      <div
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onDismiss();
        }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: SHEET_Z,
          background: 'rgba(10,10,10,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          tabIndex={-1}
          onKeyDown={onPanelKeyDown}
          style={{
            width: `min(${width}px, 100%)`,
            maxHeight: 'min(78vh, 640px)',
            overflowY: 'auto',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            outline: 'none',
          }}
        >
          {children}
        </div>
      </div>
    </ModalPortal>
  );
}

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontSize: 17,
  fontWeight: 600,
  letterSpacing: '-0.02em',
  color: 'var(--text-heading)',
};

const cancelButtonStyle: React.CSSProperties = {
  minHeight: 36,
  padding: '0 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)',
  color: 'var(--text-body)',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

export default function ConfirmSheet({
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  noteLabel,
  notePlaceholder,
  confirmPhrase,
  confirmPhraseLabel,
  pending = false,
  onCancel,
  onConfirm,
}: {
  title: string;
  body?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Red confirm button. Reserve for genuinely irreversible actions. */
  destructive?: boolean;
  /** When set, renders an optional note field and passes it to onConfirm. */
  noteLabel?: string;
  notePlaceholder?: string;
  /**
   * Type-to-confirm. The confirm button stays disabled until the user types
   * this string exactly (trimmed, case-insensitive). Reserve for the handful
   * of actions that destroy data you can't get back.
   */
  confirmPhrase?: string;
  confirmPhraseLabel?: string;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: (note?: string) => void;
}) {
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState('');
  const titleId = useId();
  const phraseOk =
    !confirmPhrase || typed.trim().toLowerCase() === confirmPhrase.trim().toLowerCase();
  return (
    <SheetShell labelledBy={titleId} width={460} onDismiss={onCancel}>
      <div style={{ padding: '18px 20px 0' }}>
        <h2 id={titleId} style={titleStyle}>
          {title}
        </h2>
        {body && (
          <div style={{ fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.55, marginTop: 8 }}>
            {body}
          </div>
        )}
      </div>

      {noteLabel && (
        <div style={{ padding: '14px 20px 0' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{noteLabel}</div>
          <Textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            placeholder={notePlaceholder}
            aria-label={noteLabel}
          />
        </div>
      )}

      {confirmPhrase && (
        <div style={{ padding: '14px 20px 0' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
            {confirmPhraseLabel ?? 'To confirm, type'}{' '}
            <strong style={{ color: 'var(--text-heading)', fontFamily: 'var(--font-mono)' }}>
              {confirmPhrase}
            </strong>
          </div>
          <TextInput
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            placeholder={confirmPhrase}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            aria-label={`Type ${confirmPhrase} to confirm`}
          />
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          padding: '16px 20px',
          marginTop: 4,
        }}
      >
        <button type="button" onClick={onCancel} style={cancelButtonStyle}>
          {cancelLabel}
        </button>
        <button
          type="button"
          disabled={pending || !phraseOk}
          onClick={() => onConfirm(note.trim() || undefined)}
          style={{
            minHeight: 36,
            padding: '0 16px',
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${destructive ? 'var(--danger)' : 'var(--brand-primary)'}`,
            background: destructive ? 'var(--danger)' : 'var(--brand-primary)',
            color: '#fff',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: pending ? 'wait' : !phraseOk ? 'not-allowed' : 'pointer',
            opacity: pending || !phraseOk ? 0.5 : 1,
          }}
        >
          {pending ? 'Working…' : confirmLabel}
        </button>
      </div>
    </SheetShell>
  );
}

/**
 * Prompt sheet, for the dialogs that collect a VALUE, not a decision.
 *
 * Replaces window.prompt at the call sites where the answer is the point: a
 * link URL in the rich-text toolbar, a smart-list name, a campaign name. A
 * native prompt can't say what shape the value should take, can't validate
 * before it closes, and on iOS Safari it can be suppressed entirely, which
 * silently turned "add a link" into a no-op.
 *
 * Same shell as ConfirmSheet on purpose. Submits on Enter for single-line.
 */
export function PromptSheet({
  title,
  body,
  label,
  placeholder,
  initialValue = '',
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  multiline = false,
  required = true,
  inputType = 'text',
  validate,
  pending = false,
  onCancel,
  onSubmit,
}: {
  title: string;
  body?: React.ReactNode;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  multiline?: boolean;
  /** When false the sheet can be submitted empty and onSubmit gets ''. */
  required?: boolean;
  inputType?: 'text' | 'url' | 'email' | 'number';
  /** Return an error string to block submission, or null to allow it. */
  validate?: (value: string) => string | null;
  pending?: boolean;
  onCancel: () => void;
  onSubmit: (value: string) => void;
}) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();

  const trimmed = value.trim();
  const canSubmit = !pending && (required ? trimmed.length > 0 : true);

  function submit() {
    if (!canSubmit) return;
    const problem = validate?.(trimmed) ?? null;
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    onSubmit(trimmed);
  }

  return (
    <SheetShell labelledBy={titleId} width={460} onDismiss={onCancel}>
      <div style={{ padding: '18px 20px 0' }}>
        <h2 id={titleId} style={titleStyle}>
          {title}
        </h2>
        {body && (
          <div style={{ fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.55, marginTop: 8 }}>
            {body}
          </div>
        )}
      </div>

      <div style={{ padding: '14px 20px 0' }}>
        {label && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
        )}
        {multiline ? (
          <Textarea
            rows={3}
            value={value}
            onChange={(e) => {
              setValue(e.currentTarget.value);
              setError(null);
            }}
            placeholder={placeholder}
            autoFocus
            aria-label={label ?? title}
          />
        ) : (
          <TextInput
            type={inputType}
            value={value}
            onChange={(e) => {
              setValue(e.currentTarget.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={placeholder}
            autoFocus
            autoComplete="off"
            aria-label={label ?? title}
          />
        )}
        {error && (
          <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }} role="alert">
            {error}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '16px 20px', marginTop: 4 }}>
        <button type="button" onClick={onCancel} style={cancelButtonStyle}>
          {cancelLabel}
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={submit}
          style={{
            minHeight: 36,
            padding: '0 16px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--brand-primary)',
            background: 'var(--brand-primary)',
            color: 'var(--text-on-brand)',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: !canSubmit ? 'not-allowed' : 'pointer',
            opacity: canSubmit ? 1 : 0.5,
          }}
        >
          {pending ? 'Working…' : confirmLabel}
        </button>
      </div>
    </SheetShell>
  );
}

/**
 * Result sheet, for reporting what a job actually did.
 *
 * Replaces window.alert on Production, which dumped per-agent email blast
 * outcomes as a wall of text you couldn't scroll, copy, or read on a phone.
 */
export function ResultSheet({
  title,
  lines,
  tone = 'neutral',
  onClose,
}: {
  title: string;
  lines: string[];
  tone?: 'neutral' | 'success' | 'danger';
  onClose: () => void;
}) {
  const titleId = useId();
  const accent =
    tone === 'success' ? 'var(--success)' : tone === 'danger' ? 'var(--danger)' : 'var(--text-heading)';
  return (
    <SheetShell labelledBy={titleId} width={520} onDismiss={onClose}>
      <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid var(--border)' }}>
        <h2
          id={titleId}
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: accent,
          }}
        >
          {title}
        </h2>
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: '10px 20px' }}>
        {lines.map((l, i) => (
          <li
            key={i}
            style={{
              fontSize: 13,
              color: 'var(--text-body)',
              padding: '5px 0',
              borderTop: i === 0 ? 'none' : '1px solid var(--border)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {l}
          </li>
        ))}
      </ul>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            minHeight: 36,
            padding: '0 16px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--brand-primary)',
            background: 'var(--brand-primary)',
            color: 'var(--text-on-brand)',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Done
        </button>
      </div>
    </SheetShell>
  );
}
